import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import Navbar from "../components/Navbar";
import DocumentsPanel from "../components/DocumentsPanel";
import DataReview from "../components/DataReview";
import DocumentViewer from "../components/DocumentViewer";
import ClaimsPanel from "../components/ClaimsPanel";
import PetitionEditor from "../components/PetitionEditor";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { statusMeta } from "../lib/status";
import { toast } from "sonner";
import { ArrowLeft, FileUp, ClipboardCheck, ListChecks, ScrollText, Loader2 } from "lucide-react";

export default function CaseWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [meta, setMeta] = useState({ field_groups: [], claims: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("documentos");

  const loadCase = async () => {
    try {
      const [caseRes, docsRes, metaRes] = await Promise.all([
        api.get(`/cases/${id}`),
        api.get(`/cases/${id}/documents`),
        api.get(`/meta`),
      ]);
      setCaseData(caseRes.data);
      setDocuments(docsRes.data);
      setMeta(metaRes.data);
    } catch (e) {
      toast.error("Caso não encontrado");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const reloadDocs = async () => {
    const res = await api.get(`/cases/${id}/documents`);
    setDocuments(res.data);
  };

  useEffect(() => { loadCase(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !caseData) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
        </div>
      </div>
    );
  }

  const sm = statusMeta(caseData.status);

  const tabs = [
    { v: "documentos", label: "Documentos", icon: FileUp },
    { v: "revisao", label: "Revisão de Dados", icon: ClipboardCheck },
    { v: "pedidos", label: "Pedidos & Jurisprudência", icon: ListChecks },
    { v: "peticao", label: "Petição", icon: ScrollText },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-4 min-w-0">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="back-btn">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0">
              <div className="flex items-center gap-3">
                <h1 className="font-display text-xl font-extrabold tracking-tight truncate">{caseData.title}</h1>
                <Badge variant="outline" className={`${sm.className} border text-[11px] font-semibold shrink-0`}>{sm.label}</Badge>
              </div>
              {caseData.client_name && <p className="text-sm text-muted-foreground">{caseData.client_name}</p>}
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white border h-auto p-1 flex-wrap">
            {tabs.map((t) => (
              <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`}
                className="data-[state=active]:bg-slate-900 data-[state=active]:text-white gap-2 px-4 py-2">
                <t.icon className="h-4 w-4" /> {t.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="documentos" className="mt-6 max-w-3xl">
            <DocumentsPanel caseId={id} documents={documents} onChange={reloadDocs}
              onExtracted={(c) => { setCaseData(c); setTab("revisao"); }} />
          </TabsContent>

          <TabsContent value="revisao" className="mt-6">
            <div className="grid lg:grid-cols-12 gap-6">
              <div className="lg:col-span-5 h-[calc(100vh-220px)] lg:sticky lg:top-20">
                <DocumentViewer documents={documents} />
              </div>
              <div className="lg:col-span-7">
                <DataReview caseData={caseData} fieldGroups={meta.field_groups}
                  onSaved={(c) => setCaseData(c)} />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pedidos" className="mt-6">
            <ClaimsPanel caseData={caseData} allClaims={meta.claims} onSaved={(c) => setCaseData(c)} />
          </TabsContent>

          <TabsContent value="peticao" className="mt-6">
            <PetitionEditor caseData={caseData} onSaved={(c) => setCaseData(c)} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
